/**
 * The signed-out frame, shared by Login and AcceptInvite. Lifted verbatim from KeyGate so
 * the one full-bleed gold surface in the product stays in exactly one file.
 *
 * `bg-gold-surface`, never `bg-gold`: --gold darkens to #8a6714 on the light theme and a
 * full-bleed panel of it reads as olive mud.
 */
import type { ReactNode, ReactElement } from "react";

export function AuthFrame({ children }: { children: ReactNode }): ReactElement {
  return (
    <main className="grid min-h-screen grid-cols-1 lg:grid-cols-[1fr_minmax(0,460px)]">
      {/* the gold band: the one full-bleed accent surface in the product */}
      <section className="grain relative hidden flex-col justify-between overflow-hidden bg-gold-surface p-10 text-on-gold lg:flex">
        <div className="relative z-[1] flex items-center justify-between border-b border-on-gold/20 pb-4 font-mono text-[10.5px] tracking-[0.25em] uppercase">
          <span className="flex items-center gap-2">
            <i className="dot pulse bg-on-gold" /> // document intelligence
          </span>
          <span>v1</span>
        </div>

        <div className="relative z-[1]">
          <h2 className="text-[clamp(2.6rem,6vw,4.6rem)] leading-[0.9] font-black tracking-tighter uppercase">
            Any document
            <br />
            in. Structured
            <br />
            data out.
          </h2>
          <p className="mt-6 max-w-[42ch] text-[13.5px] leading-relaxed text-on-gold/75">
            Three read tiers, cheapest first — the text a file already carries, then local
            OCR, then a vision model only for the pages that need one. Every value carries
            the page and the pixels it came from.
          </p>
        </div>

        <div className="relative z-[1] flex flex-wrap gap-x-6 gap-y-2 border-t border-on-gold/20 pt-4 font-mono text-[10.5px] font-bold tracking-[0.16em] uppercase">
          {["pdf", "xlsx", "docx", "pptx", "odf", "epub", "csv", "html", "eml", "images"].map(
            (format) => (
              <span key={format}>&gt; {format}</span>
            ),
          )}
        </div>
      </section>

      <section className="flex flex-col justify-center bg-ground px-8 py-12 sm:px-12">
        {/* the wordmark block */}
        <div className="mb-10 flex items-center gap-3">
          <span className="grid size-9 place-items-center bg-gold-surface text-[19px] leading-none font-black text-on-gold">
            a
          </span>
          <div className="leading-none">
            <h1 className="text-[15px] font-extrabold tracking-[0.14em] uppercase">
              alt<span className="text-gold">-</span>parser
            </h1>
            <span className="mt-1 block font-mono text-[9px] tracking-[0.22em] text-faint uppercase">
              operator console
            </span>
          </div>
        </div>

        {children}
      </section>
    </main>
  );
}
